import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { AuthService } from './auth.service';
import { DasFilters, DasOption } from '../models/dataFilter/filterPrefetchResponse.model';
import {
  FilterCatalog,
  FilterKey,
  FilterOption,
  FilterOptionsByKey,
  FilterSelection
} from '../models/dataFilter/filterCatalog.model';
import { SelectedFilters } from '../models/dataFilter/selectedFilters.model';
import { ChipGroup, FilterChip } from '../models/dataFilter/filterChip.model';

/** Top-down order — the order the cascade runs and the dialog renders. */
export const FILTER_KEYS: readonly FilterKey[] = ['businessTypes', 'processors', 'countries', 'states', 'sites'];

/** UI dimension → payload key. The one place the two vocabularies meet. */
const PAYLOAD_KEYS: Record<FilterKey, keyof SelectedFilters> = {
  businessTypes: 'businessType',
  processors: 'processor',
  countries: 'country',
  states: 'state',
  sites: 'site'
};

/**
 * The `filters` object for a request that must ignore the user's selection.
 * The backend reads an empty dimension as "no filter on it".
 */
const EMPTY_SELECTED_FILTERS = Object.fromEntries(
  FILTER_KEYS.map(key => [PAYLOAD_KEYS[key], []])
) as unknown as SelectedFilters;

/** Dimensions that are hidden, not just locked, when the account has only one option. */
const HIDDEN_WHEN_SINGLE: readonly FilterKey[] = ['businessTypes', 'processors'];

const EMPTY_SELECTION: FilterSelection = {
  businessTypes: [], processors: [], countries: [], states: [], sites: []
};

/** Placeholder labels, so the dialog renders sensibly before the pre-fetch lands. */
const EMPTY_CATALOG: FilterCatalog = {
  labels: {
    businessTypes: 'Business Type',
    processors: 'Processor',
    countries: 'Country',
    states: 'State',
    sites: 'Site'
  },
  businessTypes: [], processors: [], countries: [], states: [], sites: []
};

/** Chip sections. Labels come from the catalog, so only the grouping lives here. */
const FILTER_SECTIONS: { title: string; keys: FilterKey[] }[] = [
  { title: 'Data Scope', keys: ['businessTypes', 'processors', 'countries', 'states', 'sites'] }
];

function sameSelection(a: FilterSelection, b: FilterSelection): boolean {
  return FILTER_KEYS.every(key =>
    a[key].length === b[key].length && a[key].every((value, i) => value === b[key][i])
  );
}

const toOption = ({ value, label }: DasOption): FilterOption => ({ value, label });
const values = (options: FilterOption[]) => options.map(option => option.value);

/**
 * Owns the user-selectable data filters: the options the user may choose from,
 * the selection currently applied, the cascade rules between dimensions, and
 * the `filters` object the data APIs will receive.
 *
 * Filtering itself happens server-side — nothing here filters rows. State is in
 * memory only, so applied filters survive navigation but not a page refresh.
 *
 * Pure state and rules with no HTTP, like DataService: ApiService fetches the
 * options and pushes them in through setCatalog.
 */
@Injectable({
  providedIn: 'root'
})
export class DataFilterService {
  private readonly auth = inject(AuthService);

  private readonly _catalog = signal<FilterCatalog>(EMPTY_CATALOG);
  readonly catalog = this._catalog.asReadonly();

  /**
   * Structural equality, so re-applying an unchanged selection — opening the
   * dialog and pressing Filter without edits — doesn't notify.
   */
  private readonly _applied = signal<FilterSelection>(EMPTY_SELECTION, { equal: sameSelection });
  readonly applied = this._applied.asReadonly();

  readonly hasActiveFilters = computed(() => FILTER_KEYS.some(key => this._applied()[key].length > 0));

  /**
   * Dimensions not shown at all: Business Type and Processor when the account
   * only ever has one. Judged on the whole catalog rather than the cascade, so
   * fields never vanish mid-selection — picking a business type that holds a
   * single processor locks the Processor field instead of removing it.
   */
  readonly hidden = computed(() => {
    const catalog = this._catalog();
    return Object.fromEntries(
      FILTER_KEYS.map(key => [key, HIDDEN_WHEN_SINGLE.includes(key) && catalog[key].length === 1])
    ) as Record<FilterKey, boolean>;
  });

  catalogLoaded = signal<boolean>(false);

  constructor() {
    // Options and selections belong to a user. Logout navigates rather than
    // reloads, so without this the next person to sign in on this tab would
    // inherit the last user's applied filters. Keyed off identity, so a token
    // refresh (same user) leaves them alone; the first run is skipped so it
    // can never wipe options that are already loaded.
    let previous: string | undefined;
    effect(() => {
      const user = this.auth.email();
      if (previous !== undefined && user !== previous) {
        untracked(() => this.reset());
      }
      previous = user;
    });
  }

  /** Normalises the pre-fetch response. The only code that knows its shape. */
  setCatalog(filters: DasFilters): void {
    this._catalog.set({
      labels: {
        businessTypes: filters.businessTypes.label,
        processors: filters.processors.label,
        countries: filters.countries.label,
        states: filters.states.label,
        sites: filters.sites.label
      },
      businessTypes: filters.businessTypes.options.map(toOption),
      // `detail` is the dialog's secondary line. Which field supplies it is
      // decided here, so the template needs no per-dimension branches.
      processors: filters.processors.options.map(p => ({
        ...toOption(p),
        detail: p.value,
        businessType: p.parentValue,
        countries: p.countryValues ?? [],
        states: p.stateValues ?? [],
        status: p.status
      })),
      countries: filters.countries.options.map(toOption),
      states: filters.states.options.map(s => ({
        ...toOption(s),
        detail: s.parentValue,
        country: s.parentValue
      })),
      sites: filters.sites.options.map(s => ({
        ...toOption(s),
        detail: s.parentValue + ', ' + s.processorValue,
        state: s.parentValue,
        processor: s.processorValue
      }))
    });
    this.catalogLoaded.set(true);
  }

  apply(selection: FilterSelection): void {
    this._applied.set(selection);
  }

  /**
   * Drops one value — a chip's remove button. No re-cascade needed: removing a
   * value only ever widens the levels below, so everything still selected
   * under it stays valid.
   */
  remove(chip: FilterChip): void {
    const applied = this._applied();
    this._applied.set({
      ...applied,
      [chip.dimension]: applied[chip.dimension].filter(value => value !== chip.value)
    });
  }

  reset(): void {
    this._catalog.set(EMPTY_CATALOG);
    this._applied.set(EMPTY_SELECTION);
  }

  /** What a fresh dialog opens on — nothing chosen except single-option levels. */
  defaultSelection(): FilterSelection {
    return this.cascade(EMPTY_SELECTION).selection;
  }

  /**
   * One top-down pass over the hierarchy: at each level, the options still
   * reachable from the levels above, and the selection corrected against them
   * — a level with exactly one option is auto-selected, and selections no
   * longer reachable are dropped.
   *
   * Each level narrows the next through its effective set: what's selected
   * there, or everything still reachable when nothing is. That is what lets a
   * business type with no processor chosen still narrow the countries.
   */
  cascade(selection: FilterSelection): { options: FilterOptionsByKey; selection: FilterSelection } {
    const catalog = this._catalog();
    const fit = (available: string[], selected: string[]) =>
      available.length === 1 ? [available[0]] : selected.filter(value => available.includes(value));
    const effective = (available: string[], selected: string[]) => selected.length ? selected : available;

    // Business type — the root, never narrowed.
    const businessTypes = catalog.businessTypes;
    const selectedBusinessTypes = fit(values(businessTypes), selection.businessTypes);
    const effectiveBusinessTypes = effective(values(businessTypes), selectedBusinessTypes);

    // Processor — under an effective business type.
    const processors = catalog.processors.filter(p => effectiveBusinessTypes.includes(p.businessType));
    const selectedProcessors = fit(values(processors), selection.processors);
    const effectiveProcessorValues = effective(values(processors), selectedProcessors);
    const effectiveProcessors = processors.filter(p => effectiveProcessorValues.includes(p.value));

    // Country — one an effective processor operates in. The link is held on
    // the processor, not the country.
    const countries = catalog.countries.filter(country =>
      effectiveProcessors.some(p => p.countries.includes(country.value)));
    const selectedCountries = fit(values(countries), selection.countries);
    const effectiveCountries = effective(values(countries), selectedCountries);

    // State — in an effective country *and* covered by an effective processor.
    const states = catalog.states.filter(state =>
      effectiveCountries.includes(state.country) &&
      effectiveProcessors.some(p => p.states.includes(state.value)));
    const selectedStates = fit(values(states), selection.states);
    const effectiveStates = effective(values(states), selectedStates);

    // Site — in an effective state *and* belonging to an effective processor.
    const sites = catalog.sites.filter(site =>
      effectiveStates.includes(site.state) && effectiveProcessorValues.includes(site.processor));
    const selectedSites = fit(values(sites), selection.sites);

    return {
      options: { businessTypes, processors, countries, states, sites },
      selection: {
        businessTypes: selectedBusinessTypes,
        processors: selectedProcessors,
        countries: selectedCountries,
        states: selectedStates,
        sites: selectedSites
      }
    };
  }

  /** value → label per dimension, so chips show "Acme Dallas" rather than "site-1001". */
  private readonly optionLabels = computed(() => {
    const catalog = this._catalog();
    return Object.fromEntries(
      FILTER_KEYS.map(key => [key, new Map(catalog[key].map(option => [option.value, option.label]))])
    ) as Record<FilterKey, Map<string, string>>;
  });

  /**
   * The applied selection as chip groups. Hidden dimensions get no chips: the
   * user never saw a field for them, and removing the chip would only bring it
   * back on the next Filter.
   */
  readonly chipGroups = computed<ChipGroup[]>(() => {
    const applied = this._applied();
    const hidden = this.hidden();
    const { labels } = this._catalog();
    const optionLabels = this.optionLabels();
    return FILTER_SECTIONS
      .map(section => ({
        title: section.title,
        subGroups: section.keys
          .filter(key => !hidden[key])
          .map(key => ({
            title: labels[key],
            chips: applied[key].map(value => ({
              dimension: key,
              value,
              label: optionLabels[key].get(value) ?? value
            }))
          }))
          .filter(sub => sub.chips.length > 0)
      }))
      .filter(group => group.subGroups.length > 0);
  });

  /** The user's selection in payload form — every key always present. */
  readonly selectedFilters = computed<SelectedFilters>(() => {
    const applied = this._applied();
    return Object.fromEntries(
      FILTER_KEYS.map(key => [PAYLOAD_KEYS[key], applied[key]])
    ) as unknown as SelectedFilters;
  });

  /**
   * The `filters` object a request should carry: the user's selection, or the
   * same shape with every dimension empty for a request that must ignore it.
   * Read at call time, so each request snapshots the selection as it stands.
   */
  requestFilters(useSelectedFilters: boolean): SelectedFilters {
    return useSelectedFilters ? this.selectedFilters() : EMPTY_SELECTED_FILTERS;
  }
}
